import { memo, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DEFAULT_TIMER, formatTime } from '../../../../utils/time';

import styles from './TournamentTimer.module.scss';

type Props = {
  startDate: string;
  endDate: string;
  to: 'start' | 'end';
  theme?: 'black' | 'purple';
  isOnWidget?: boolean;
  className?: string;
  onComplete: () => void;
  timerFormat?: 'short' | 'long';
};
const TournamentTimer: React.FC<Props> = memo(
  ({ startDate, endDate, to, theme = 'black', isOnWidget, className, onComplete, timerFormat }) => {
    const { t } = useTranslation();
    const destinationTime = to === 'start' ? startDate : endDate;
    const secondsToDate = dayjs(destinationTime).diff(dayjs(), 'seconds');

    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const [timer, setTimer] = useState<string>(secondsToDate > 0 ? formatTime(secondsToDate, 's', t) : DEFAULT_TIMER);

    useEffect(() => {
      const interval = setInterval(() => {
        const secsToDate = dayjs(destinationTime).diff(dayjs(), 'seconds');
        if (secsToDate <= 0) {
          setTimer(DEFAULT_TIMER);
          clearInterval(interval);
          onCompleteRef.current();
        } else {
          const timerFormatted = formatTime(secsToDate, 's', t);
          setTimer(timerFormatted);
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [destinationTime, timerFormat, t]);

    return (
      <div
        className={classNames(
          styles.tournamentTimer,
          isOnWidget ? styles.widget : undefined,
          theme === 'purple' ? styles.themePurple : undefined,
          className,
        )}
        data-testid={isOnWidget ? 'tournament-widget-timer' : 'tournament-timer'}
      >
        {timer}
      </div>
    );
  },
);

TournamentTimer.displayName = 'TournamentTimer';

export default TournamentTimer;
